import { FilterPopoverTabs } from '@/Enums/UserManagement/FilterPopoverTabs.enum';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { AdvertisersTabButton, ClientsTabButton, WhitelabelsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/userManagement';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { useAppSelector } from '@/store/hooks';
import { selectHideWhitelabels, selectMeRole } from '@/store/selectors/me.selectors';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { MouseEventHandler, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

interface IProps {
  setFilters: (value: Partial<Record<string, unknown>>) => void;
}

const FilterPopover = ({ setFilters }: IProps) => {
  const { clients, whitelabels, orgs, filters, clientsRequestId, whitelabelsRequestId } = useAppSelector(
    state => state.userManagement,
  );
  const meRole = useAppSelector(selectMeRole);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentTab, setCurrentTab] = useState(FilterPopoverTabs.Clients);

  const [pendingWhitelabels, setPendingWhitelabels] = useState(filters.whitelabels);
  const [pendingClients, setPendingClients] = useState(filters.clients);
  const [pendingAdvertisers, setPendingAdvertisers] = useState(filters.advertisers);

  const hideWhitelabelLevel = useAppSelector(selectHideWhitelabels);

  const FILTER_TABS = useMemo(
    () =>
      hideWhitelabelLevel
        ? [FilterPopoverTabs.Clients, FilterPopoverTabs.Advertisers]
        : [FilterPopoverTabs.Whitelabels, FilterPopoverTabs.Clients, FilterPopoverTabs.Advertisers],
    [hideWhitelabelLevel],
  );

  const filtersOpen = useMemo(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const options = useMemo(() => {
    return getFilterOptions({
      userRole: meRole,
      clients,
      selectedClients: pendingClients,
      selectedWhitelabels: pendingWhitelabels,
      whitelabels: [...whitelabels, ...orgs],
    });
  }, [clients, meRole, orgs, pendingClients, pendingWhitelabels, whitelabels]);

  useEffect(() => {
    setCurrentTab(FILTER_TABS[0]);
  }, [FILTER_TABS]);

  const resetPendingValues = useCallback(() => {
    setPendingWhitelabels(filters.whitelabels);
    setPendingClients(filters.clients);
    setPendingAdvertisers(filters.advertisers);
  }, [filters.advertisers, filters.clients, filters.whitelabels]);

  const handleTabChange = useCallback((_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  }, []);

  const handleOpen = useCallback<MouseEventHandler<HTMLButtonElement>>(
    ({ currentTarget }) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues],
  );

  const closeModal = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FILTER_TABS[0]);
  }, [FILTER_TABS, resetPendingValues]);

  const handleClose = useCallback(
    (_: SyntheticEvent, reason: string) => {
      if (reason) return;
      closeModal();
    },
    [closeModal],
  );

  const handleSubmit = useCallback(() => {
    if (
      areArraysOfObjectsEqual(filters.whitelabels, pendingWhitelabels) &&
      areArraysOfObjectsEqual(filters.clients, pendingClients) &&
      areArraysOfObjectsEqual(filters.advertisers, pendingAdvertisers)
    ) {
      closeModal();
      return;
    }
    setFilters({
      whitelabels: pendingWhitelabels,
      clients: pendingClients,
      advertisers: pendingAdvertisers,
    });
    closeModal();
  }, [
    filters.advertisers,
    filters.clients,
    filters.whitelabels,
    closeModal,
    pendingAdvertisers,
    pendingClients,
    pendingWhitelabels,
    setFilters,
  ]);

  const updateWhitelabelsValue = useCallback((updatedWhitelabels = []) => {
    setPendingWhitelabels(updatedWhitelabels);
    setPendingClients([]);
    setPendingAdvertisers([]);
  }, []);

  const updateClientsValue = useCallback((updatedClients = []) => {
    setPendingClients(updatedClients);
    setPendingAdvertisers([]);
  }, []);

  const updateAdvertisersValue = useCallback((updatedAdvertisers = []) => {
    setPendingAdvertisers(updatedAdvertisers);
  }, []);

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} variant='outlined' />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='pacing filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          {hideWhitelabelLevel ? null : (
            <Tab
              value={FilterPopoverTabs.Whitelabels}
              component={WhitelabelsTabButton}
              {...a11yProps(FilterPopoverTabs.Whitelabels, BASE_TAB_ID)}
            />
          )}
          <Tab
            value={FilterPopoverTabs.Clients}
            component={ClientsTabButton}
            {...a11yProps(FilterPopoverTabs.Clients, BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Advertisers}
            component={AdvertisersTabButton}
            {...a11yProps(FilterPopoverTabs.Advertisers, BASE_TAB_ID)}
          />
        </Tabs>
        {hideWhitelabelLevel ? null : (
          <TabPanel className='w-full' baseTabId={BASE_TAB_ID} value={currentTab} index={FilterPopoverTabs.Whitelabels}>
            <TabSelect
              label='Whitelabels'
              loading={!!whitelabelsRequestId}
              options={options.whitelabel}
              value={pendingWhitelabels}
              onChange={updateWhitelabelsValue}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          </TabPanel>
        )}
        <TabPanel className='w-full' baseTabId={BASE_TAB_ID} value={currentTab} index={FilterPopoverTabs.Clients}>
          <TabSelect
            label='Client'
            loading={!!clientsRequestId}
            options={options.client}
            value={pendingClients}
            onChange={updateClientsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel className='w-full' baseTabId={BASE_TAB_ID} value={currentTab} index={FilterPopoverTabs.Advertisers}>
          <TabSelect
            label='Advertiser'
            loading={!!clientsRequestId}
            options={options.advertiser}
            value={pendingAdvertisers}
            onChange={updateAdvertisersValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
